import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subject } from 'rxjs';

/** Clears loaders owned by the outgoing route, including pending presentations. */
@Injectable({ providedIn: 'root' })
export class RouteLoadingService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stale = new WeakSet<HTMLIonLoadingElement>();
  private readonly dismissing = new WeakSet<HTMLIonLoadingElement>();
  private readonly dismissedSubject = new Subject<void>();
  readonly dismissed = this.dismissedSubject.asObservable();

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
      takeUntilDestroyed(),
    ).subscribe(() => {
      // Ionic hoists inline loaders under ion-app, outside the cached page.
      // Snapshot before the destination page creates any of its own loaders.
      for (const loader of Array.from(this.document.querySelectorAll<HTMLIonLoadingElement>('ion-loading'))) {
        this.stale.add(loader);
        void this.dismissLoader(loader);
      }
    });
    const dismissLateLoader = (event: Event): void => {
      const loader = event.target as HTMLIonLoadingElement;
      if (this.stale.has(loader)) void this.dismissLoader(loader);
    };
    this.document.addEventListener('ionLoadingDidPresent', dismissLateLoader);
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('ionLoadingDidPresent', dismissLateLoader);
      this.dismissedSubject.complete();
    });
  }

  private async dismissLoader(loader: HTMLIonLoadingElement): Promise<void> {
    if (this.dismissing.has(loader)) return;
    this.dismissing.add(loader);
    try {
      const dismissed = await loader.dismiss();
      // didDismiss fires before Ionic removes the overlay and releases focus.
      // Wait for the complete dismissal promise before asking Home to focus.
      if (dismissed && !this.destroyRef.destroyed) this.dismissedSubject.next();
    } finally {
      this.dismissing.delete(loader);
    }
  }
}
